// ===== Packages =====

import styled               from 'styled-components';

// ===== Services =====

import {
    setColorLightness,
}                           from '../../services';

// ===== Constants =====

import {
    CONTENTS_TABLE_Z_INDEX,
    CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_X_OFFSET_SMALL_VIEWPORT,
    CONTENTS_TABLE_HOVER_LIGHTNESS_VALUE,
    READER_VIEW_OVERLAY_Z_INDEX,
    BODY_FONT_SIZE,
    BUTTON_ICON_MULTIPLIER,
}                           from '../../constants/generalConstants';
import FONT_TYPE            from '../../constants/fontType';

interface ContentsTableProps {
    widthExpanded: number,
    widthContracted: number,
    margin: number,
    heightExpanded: number,
    topExpanded: number,
    contentsTableVisible: boolean,
    showWarning: boolean,
    showDetailMinimumWarning: boolean,
    showSteering: boolean,
    notifyUserToSignUpAfterAnnotationCreationAttempt: boolean,
    transitionDuration: number,
}
export const ContentsTable = styled.div<ContentsTableProps>`
    position: fixed;
    top: ${({ topExpanded }) => `var(--reader-contents-table-top, ${topExpanded}px)`};
    height: ${({ heightExpanded, margin }) => `var(--reader-contents-table-height, ${(heightExpanded - 2 * margin)}px)`};
    width: ${({
        contentsTableVisible,
        widthExpanded,
        widthContracted,
    }) => `${contentsTableVisible ? widthExpanded : widthContracted}px`};
    margin: ${({ margin }) => `${margin}px`};
    background: ${({
        contentsTableVisible,
        showWarning,
        showDetailMinimumWarning,
        showSteering,
        theme,
    }) => {
        // Warning states take precedence over steering/read-mode backgrounds.
        if (showDetailMinimumWarning) {
            return theme.verascopeColor.red300;
        }

        if (showWarning) {
            return theme.verascopeColor.red200;
        }

        if (showSteering) {
            return theme.verascopeColor.purple400;
        }

        if (contentsTableVisible) {
            return theme.color.cream400;
        }

        return theme.color.cream400;
    }};
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    box-shadow: ${({ contentsTableVisible, theme }) => (contentsTableVisible
        ? theme.color.boxShadow100
        : theme.color.boxShadow000
    )};
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch; /* smooth scrolling for iOS */
    touch-action: pan-y;
    z-index: ${({ notifyUserToSignUpAfterAnnotationCreationAttempt }) => (notifyUserToSignUpAfterAnnotationCreationAttempt
        ? READER_VIEW_OVERLAY_Z_INDEX - 1
        : CONTENTS_TABLE_Z_INDEX)};
    transition: ${({
        theme,
        transitionDuration,
        showWarning,
        showDetailMinimumWarning,
        showSteering,
    }) => `
        width ${transitionDuration}ms ${theme.motion.eagerEasing},
        /* Steering/error feedback should apply immediately, without easing lag. */
        background ${showWarning || showDetailMinimumWarning || showSteering ? 0 : transitionDuration}ms ${theme.motion.eagerEasing},
        box-shadow ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

interface ContentsTableToggleButtonContainerProps {
    isVisible: boolean,
    tableVisible: boolean,
    leftExpanded: number,
    leftContracted: number,
    height: number,
    topExpanded: number,
    notifyUserToSignUpAfterAnnotationCreationAttempt: boolean,
    transitionDuration: number,
}
export const ContentsTableToggleButtonContainer = styled.div<ContentsTableToggleButtonContainerProps>`
    position: fixed;
    left: ${({
        tableVisible,
        leftExpanded,
        leftContracted,
    }) => `${tableVisible ? leftExpanded : leftContracted}px`};
    top: ${({ topExpanded }) => `var(--reader-contents-toggle-top, ${topExpanded}px)`};
    background: ${({ theme }) => theme.color.white};
    border-radius: ${({ height }) => `${height / 2}px`};
    box-shadow: ${({ theme }) => theme.color.boxShadow300};
    opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
    pointer-events: ${({ isVisible }) => (isVisible ? 'auto' : 'none')};
    z-index: ${({ notifyUserToSignUpAfterAnnotationCreationAttempt }) => (notifyUserToSignUpAfterAnnotationCreationAttempt
        ? READER_VIEW_OVERLAY_Z_INDEX - 1
        : CONTENTS_TABLE_Z_INDEX)};
    transition: ${({ theme, transitionDuration }) => `
        left ${transitionDuration}ms ${theme.motion.eagerEasing},
        top ${transitionDuration}ms ${theme.motion.eagerEasing},
        opacity ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & svg {
        transform: ${({ tableVisible }) => `rotate(${tableVisible ? 180 : 0}deg)`};
        transition: ${({ theme, transitionDuration }) => `
            transform ${transitionDuration}ms ${theme.motion.eagerEasing}
        `};
    }

    ${({ theme }) => theme.mediaQuery.extraSmall} {
        // top: Modified directly using ref in ReaderView
        left: ${({
        tableVisible,
        leftExpanded,
        leftContracted,
    }) => `${tableVisible
        ? leftExpanded + CONTENTS_TABLE_TOGGLE_BUTTON_CONTAINER_X_OFFSET_SMALL_VIEWPORT
        : leftContracted}px`};
    }
`;

export const ContentsTableProgressBarContainer = styled.div`
    position: absolute;
    top: 0; // Modified directly using ref in ReaderView
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
`;

interface ContentsTableProgressBarProps {
    contentsTableVisible: boolean,
    transitionDuration: number,
}
export const ContentsTableProgressBar = styled.div<ContentsTableProgressBarProps>`
    position: absolute;
    top: 0;
    left: 0;
    height: var(--reader-contents-progress-height, 0%);
    width: 100%;
    background: ${({ theme }) => theme.color.cream300};
    opacity: ${({ contentsTableVisible }) => (contentsTableVisible ? 0.2 : 0.6)};
    transition: ${({ transitionDuration, theme }) => `
        opacitty ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};
`;

interface ContentsTableTitleProps {
    fontMultiplier: number,
    paddingTop: number,
    lineHeight: number,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
}
export const ContentsTableTitle = styled.h4<ContentsTableTitleProps>`
    margin: 0;
    padding: ${({ paddingTop }) => `${paddingTop}px ${0.9375 * BODY_FONT_SIZE}px`};
    padding-bottom: 0px;
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 600;
    line-height: ${({ lineHeight }) => `${lineHeight}em`};
    color: ${({ steeringReadOnly, warningReadOnly, theme }) => {
        if (warningReadOnly) {
            return theme.color.cream100;
        }

        if (steeringReadOnly) {
            return theme.color.white;
        }

        return theme.color.cream100;
    }};
    text-transform: uppercase;
`;

export const ContentsTableChaptersContainer = styled.div`
    background: inherit;
`;

export const ContentsTableOutlineContainer = styled.div`
    padding: ${`${0.625 * BODY_FONT_SIZE}px ${0.9375 * BODY_FONT_SIZE}px`};
    background: inherit;
`;

export const ContentsTableChapter = styled.div``;

interface ContentsTableChapterHeadProps {
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    hoverable: boolean,
    isMobile: boolean,
    padding: number,
    customCursorActive?: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterHead = styled.a<ContentsTableChapterHeadProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 100%;
    border-left: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (!selected) return `${0.1875 * BODY_FONT_SIZE}px solid transparent`;
        if (warningReadOnly) return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.yellow200}`;
        if (steeringReadOnly) return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.orange200}`;
        return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.color.cream300}`;
    }};
    background: transparent;
    padding: ${({ padding }) => `${padding}px`};
    color: inherit;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    text-decoration: none;
    user-select: none;
    -webkit-user-select: none;
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        background ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};

    ${({ hoverable, isMobile, theme }) => hoverable && !isMobile && `
        &:hover {
            background: ${setColorLightness(
        theme.color.cream300,
        CONTENTS_TABLE_HOVER_LIGHTNESS_VALUE,
    )};
        }
    `}
`;

export const ContentsTableChapterTitleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;

interface ContentsTableChapterTitleProps {
    selected: boolean,
    fontMultiplier: number,
    lineHeight: number,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterTitle = styled.div<ContentsTableChapterTitleProps>`
    margin: 0;
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: 700;
    color: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (warningReadOnly) {
            return theme.verascopeColor.red100;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return theme.verascopeColor.purple200;
        }

        return selected ? theme.color.cream300 : theme.color.cream100;
    }};
    line-height: ${({ lineHeight }) => `${lineHeight}em`};
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        color ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing},
        font-weight ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};
`;

interface ContentsTableLockIconProps {
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
}
export const ContentsTableLockIcon = styled.div<ContentsTableLockIconProps>`
    position: relative;
    top: ${`${0.05 * BODY_FONT_SIZE}px`};
    display: inline-block;
    vertical-align: text-top;
    width: ${`${1 * BODY_FONT_SIZE}px`};
    height: ${`${1 * BODY_FONT_SIZE}px`};
    margin-left: ${`${0.15 * BODY_FONT_SIZE}px`};

    & svg, & div {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & path {
        fill: ${(props) => {
        const {
            selected,
            steeringReadOnly,
            warningReadOnly,
            theme,
        } = props;

        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return theme.verascopeColor.purple500;
        }

        if (warningReadOnly) {
            return theme.verascopeColor.red400;
        }

        return theme.color.cream300;
    }};
    }
`;

interface ContentsTableChapterDescriptionProps {
    selected: boolean,
    fontMultiplier: number,
    lineHeight: number,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterDescription = styled.p<ContentsTableChapterDescriptionProps>`
    margin: 0;
    margin-left: ${`${0.9375 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: ${({ selected }) => (selected ? 600 : 400)};
    color: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (warningReadOnly) {
            return theme.verascopeColor.red400;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return theme.verascopeColor.purple500;
        }

        return theme.color.cream300;
    }};
    line-height: ${({ lineHeight }) => `${lineHeight}em`};
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        color ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};
`;

interface ContentsTableAccordionIconProps {
    customCursorActive: boolean,
    expanded: boolean,
    transitionDuration: number,
}
export const ContentsTableAccordionIcon = styled.div<ContentsTableAccordionIconProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${`${1.25 * BODY_FONT_SIZE}px`};
    height: ${`${1.25 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.625 * BODY_FONT_SIZE}px`};
    background: ${({ theme }) => theme.color.cream500};
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    transform: ${({ expanded }) => `rotate(${expanded ? 90 : 0}deg)`};
    transition: ${({ transitionDuration, theme }) => `
        transform ${transitionDuration}ms ${theme.motion.eagerEasing},
        fill ${transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & svg {
        position: relative;
        height: ${`${BUTTON_ICON_MULTIPLIER * 100}%`};
        width: ${`${BUTTON_ICON_MULTIPLIER * 100}%`};
        margin: 0;
    }

    & path {
        fill: ${({ theme }) => theme.color.cream300};
    }

    &:hover {
        & path {
            fill: ${({ theme }) => theme.verascopeColor.purple400};
        }
    }
`;

export const ContentsTableChapterAnnotationContainer = styled.div`
    position: absolute;
    right: ${`${0.9375 * BODY_FONT_SIZE}px`};
    bottom: ${`${0.625 * BODY_FONT_SIZE}px`};
    display: flex;
    flex-direction: row;
    align-items: center;
`;

interface ContentsTableChapterAnnotationIconProps {
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterAnnotationIcon = styled.div<ContentsTableChapterAnnotationIconProps>`
    height: ${`${0.9375 * BODY_FONT_SIZE}px`};
    width: ${`${0.9375 * BODY_FONT_SIZE}px`};
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        fill ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.eagerEasing}
    `};

    & svg, & div {
        position: relative;
        height: 100%;
        width: 100%;
        margin: 0;
    }

    & path {
        fill: ${(props) => {
        const {
            selected,
            steeringReadOnly,
            warningReadOnly,
            theme,
        } = props;

        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return theme.verascopeColor.purple500;
        }

        if (warningReadOnly) {
            return theme.color.white;
        }

        return theme.color.cream300;
    }};
    }
`;

interface ContentsTableChapterAnnotationTextProps {
    selected: boolean,
    fontMultiplier: number,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterAnnotationText = styled.span<ContentsTableChapterAnnotationTextProps>`
    margin-left: ${`${0.3125 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: ${({ selected }) => (selected
        ? 700
        : 500
    )};
    color: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (warningReadOnly) {
            return theme.color.white;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return selected ? theme.color.white : theme.color.cream100;
        }

        return selected ? theme.color.cream300 : theme.color.cream100;
    }};
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        color ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};
`;

interface ContentsTableChapterBodyProps {
    visible: boolean,
    heightExpanded: number,
    transitionDuration: number,
    transitionDelayDuration: number,
}
export const ContentsTableChapterBody = styled.div<ContentsTableChapterBodyProps>`
    padding-left: ${`${1.875 * BODY_FONT_SIZE}px`};
    height: ${({ heightExpanded, visible }) => `${visible ? heightExpanded : 0}px`};
    transition: ${({
        visible,
        transitionDuration,
        transitionDelayDuration,
        theme,
    }) => `
        height ${transitionDuration}ms ${visible ? 0 : transitionDelayDuration}ms ${theme.motion.standardEasing}
    `};
`;

interface ContentsTableChapterSectionProps {
    customCursorActive: boolean,
    isMobile: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterSection = styled.a<ContentsTableChapterSectionProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    color: inherit;
    cursor: ${({ customCursorActive }) => (customCursorActive ? 'none' : 'pointer')};
    text-decoration: none;
    user-select: none;
    -webkit-user-select: none;
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        background ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};

    ${({ isMobile, theme }) => !isMobile && `
        &:hover {
            background: ${setColorLightness(
        theme.color.cream300,
        CONTENTS_TABLE_HOVER_LIGHTNESS_VALUE,
    )};
        }
    `}
`;

interface ContentsTableChapterSectionTextProps {
    selected: boolean,
    fontMultiplier: number,
    lineHeight: number,
    padding: number,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterSectionText = styled.div<ContentsTableChapterSectionTextProps>`
    margin: 0;
    padding: ${({ padding }) => `${padding}px`};
    padding-left: ${`${0.625 * BODY_FONT_SIZE}px`};
    font-family: ${FONT_TYPE.SOURCE_SANS_3};
    font-size: ${({ fontMultiplier }) => `${fontMultiplier}em`};
    font-weight: ${({ selected }) => (selected
        ? 700
        : 400
    )};
    color: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (warningReadOnly && selected) {
            return theme.verascopeColor.yellow200;
        }

        if (warningReadOnly) {
            return theme.verascopeColor.red300;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        if (steeringReadOnly) {
            return theme.verascopeColor.purple200;
        }

        return selected ? theme.color.cream300 : theme.verascopeColor.purple100;
    }};
    line-height: ${({ lineHeight }) => `${lineHeight}em`};
    border-left: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (!selected) return `${0.1875 * BODY_FONT_SIZE}px solid transparent`;
        if (warningReadOnly) return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.yellow200}`;
        if (steeringReadOnly) return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.verascopeColor.orange200}`;
        return `${0.1875 * BODY_FONT_SIZE}px solid ${theme.color.cream300}`;
    }};
    transition: ${({
        transitionDuration,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => `
        color ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing},
        font-weight ${(steeringReadOnly || warningReadOnly) ? 0 : transitionDuration}ms ${theme.motion.standardEasing}
    `};
`;

interface ContentsTableChapterSectionLineProps {
    visible: boolean,
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
}
export const ContentsTableChapterSectionLine = styled.div<ContentsTableChapterSectionLineProps>`
    flex: 1;
    position: relative;
    top: ${`${0.125 * BODY_FONT_SIZE}px`};
    height: ${({ selected, visible }) => {
        if (selected && visible) {
            return '2px';
        }

        if (visible) {
            return '1px';
        }

        return '0px';
    }};
    margin: ${`0px ${0.3125 * BODY_FONT_SIZE}px`};
    background: ${({
        selected,
        steeringReadOnly,
        warningReadOnly,
        theme,
    }) => {
        if (warningReadOnly && selected) {
            return theme.verascopeColor.orange300;
        }

        if (steeringReadOnly && selected) {
            return theme.verascopeColor.orange200;
        }

        return selected ? theme.color.cream300 : theme.color.cream200;
    }};
`;

export const ContentsTableChapterSectionAnnotationContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-right: ${`${0.9375 * BODY_FONT_SIZE}px`};
`;

interface ContentsTableChapterSectionAnnotationIconProps {
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterSectionAnnotationIcon = styled(ContentsTableChapterAnnotationIcon)<ContentsTableChapterSectionAnnotationIconProps>``;

interface ContentsTableChapterSectionAnnotationTextProps {
    selected: boolean,
    steeringReadOnly: boolean,
    warningReadOnly: boolean,
    transitionDuration: number,
}
export const ContentsTableChapterSectionAnnotationText = styled(ContentsTableChapterAnnotationText)<ContentsTableChapterSectionAnnotationTextProps>``;

interface AddChapterButtonProps {
    disabled: boolean,
    transitionDuration: number,
}
export const AddChapterButton = styled.div<AddChapterButtonProps>`
    position: relative;
    width: ${`${1.875 * BODY_FONT_SIZE}px`};
    height: ${`${1.875 * BODY_FONT_SIZE}px`};
    border-radius: ${`${0.9375 * BODY_FONT_SIZE}px`};
    background: ${({ theme, disabled }) => (disabled ? theme.color.neutral100 : theme.verascopeColor.orange200)};
    box-shadow: ${({ theme, disabled }) => (disabled ? 'none' : theme.color.boxShadow100)};
    transition: ${({ theme, transitionDuration }) => `
        box-shadow ${transitionDuration}ms ${theme.motion.standardEasing}
    `};

    & svg {
        fill: ${({
        disabled,
        theme,
    }) => {
        if (disabled) {
            return `${theme.color.neutral800} !important`;
        }
    }}
    };
`;

export const AddChapterButtonIcon = styled.div`
    display: inline-block;
    position: relative;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: ${`${1.375 * BODY_FONT_SIZE}px`};
    height: ${`${1.375 * BODY_FONT_SIZE}px`};
    transition: ${({ theme }) => `transform 300ms ${theme.motion.standardEasing}`};

    & div {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    & svg {
        fill: ${({ theme }) => theme.color.white};
        width: 100%;
        height: 100%;
    }

    ${AddChapterButton}:hover & {
        transform: translate(-50%, -50%) rotate(180deg);
    }
`;
